import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2, Copy, RefreshCw } from "lucide-react";
import { BATH_CANONICAL } from "@/lib/bathImageMap";
import { toast } from "@/hooks/use-toast";

/**
 * /:lang/bath-sanitary/image-status
 *
 * Internal-style upload checklist for the Bath & Sanitary image library.
 * Probes every canonical bath image, groups results by product, and surfaces
 * the exact filename you must upload to /public/images/bath/<file>.
 */

type Slot = { key: string; path: string; label: string };
type ProductGroup = { product: string; slug?: string; slots: Slot[] };

const GROUPS: ProductGroup[] = [
  {
    product: "ARIA Sculpt Bathtub",
    slug: "aria-sculpt",
    slots: [
      { key: "ariaSculptHero", path: BATH_CANONICAL.ariaSculptHero, label: "Hero & featured" },
      { key: "ariaSculptDetail", path: BATH_CANONICAL.ariaSculptDetail, label: "Fluted exterior detail" },
      { key: "ariaSculptTop", path: BATH_CANONICAL.ariaSculptTop, label: "Matte white top-down" },
      { key: "ariaSculptNoir", path: BATH_CANONICAL.ariaSculptNoir, label: "Matte black noir side view" },
      { key: "ariaSculptNoirTop", path: BATH_CANONICAL.ariaSculptNoirTop, label: "Matte black noir top-down" },
    ],
  },
  {
    product: "ARIA Diamond Bathtub",
    slug: "aria-diamond",
    slots: [
      { key: "ariaDiamondHero", path: BATH_CANONICAL.ariaDiamondHero, label: "Hero" },
      { key: "ariaDiamondDetail", path: BATH_CANONICAL.ariaDiamondDetail, label: "Diamond hammered texture" },
      { key: "ariaDiamondTop", path: BATH_CANONICAL.ariaDiamondTop, label: "Top-down view" },
      { key: "ariaDiamondNoir", path: BATH_CANONICAL.ariaDiamondNoir, label: "Matte black noir finish" },
      { key: "ariaDiamondSuite", path: BATH_CANONICAL.ariaDiamondSuite, label: "Hotel spa suite lifestyle" },
    ],
  },
  {
    product: "ARIA Monolith Tub",
    slug: "aria-monolith",
    slots: [
      { key: "ariaMonolith", path: BATH_CANONICAL.ariaMonolith, label: "Hero" },
      { key: "ariaMonolithDetail", path: BATH_CANONICAL.ariaMonolithDetail, label: "Seamless cast rim" },
      { key: "ariaMonolithSide", path: BATH_CANONICAL.ariaMonolithSide, label: "Side profile" },
      { key: "ariaMonolithSuite", path: BATH_CANONICAL.ariaMonolithSuite, label: "Hotel suite lifestyle" },
      { key: "ariaMonolithVilla", path: BATH_CANONICAL.ariaMonolithVilla, label: "Private villa lifestyle" },
    ],
  },
  {
    product: "VENA Counter Basin",
    slug: "vena-basin",
    slots: [
      { key: "venaBasin", path: BATH_CANONICAL.venaBasin, label: "Hero" },
      { key: "venaBasinDetail", path: BATH_CANONICAL.venaBasinDetail, label: "Bowl detail" },
      { key: "venaBasinTop", path: BATH_CANONICAL.venaBasinTop, label: "Top-down view" },
      { key: "venaBasinSuite", path: BATH_CANONICAL.venaBasinSuite, label: "Hotel suite vanity" },
      { key: "venaBasinResidence", path: BATH_CANONICAL.venaBasinResidence, label: "Powder room lifestyle" },
    ],
  },
  {
    product: "VENA Vanity Console",
    slug: "vena-vanity",
    slots: [
      { key: "venaVanity", path: BATH_CANONICAL.venaVanity, label: "Hero" },
      { key: "venaVanityDetail", path: BATH_CANONICAL.venaVanityDetail, label: "Integrated basin detail" },
      { key: "venaVanitySide", path: BATH_CANONICAL.venaVanitySide, label: "Wall-hung side profile" },
      { key: "venaVanitySuite", path: BATH_CANONICAL.venaVanitySuite, label: "Hotel suite lifestyle" },
      { key: "venaVanityResidence", path: BATH_CANONICAL.venaVanityResidence, label: "Residence lifestyle" },
    ],
  },
  {
    product: "Shared Lifestyle Scenes",
    slots: [
      { key: "lifestyleSuite", path: BATH_CANONICAL.lifestyleSuite, label: "Hotel suite (shared)" },
      { key: "lifestyleResidence", path: BATH_CANONICAL.lifestyleResidence, label: "Residence (shared)" },
    ],
  },
];

type Status = "pending" | "ok" | "missing";

/** Probe an image URL via GET (HEAD on dev SPAs often returns the index.html shell). */
async function probe(path: string): Promise<Status> {
  try {
    const res = await fetch(path + "?_probe=" + Date.now(), { method: "GET", cache: "no-store" });
    if (!res.ok) return "missing";
    const ct = res.headers.get("content-type") || "";
    // SPA fallback returns HTML for missing static files — treat as missing.
    if (!ct.startsWith("image/")) return "missing";
    const len = Number(res.headers.get("content-length") || "0");
    if (len > 0 && len < 200) return "missing"; // suspiciously small
    return "ok";
  } catch {
    return "missing";
  }
}

const BathImageStatus = () => {
  const [results, setResults] = useState<Record<string, Status>>({});
  const [scanning, setScanning] = useState(false);

  const runScan = async () => {
    setScanning(true);
    const init: Record<string, Status> = {};
    GROUPS.forEach((g) => g.slots.forEach((s) => (init[s.key] = "pending")));
    setResults(init);

    const all = GROUPS.flatMap((g) => g.slots);
    // Probe in small batches to be polite.
    for (let i = 0; i < all.length; i += 4) {
      const batch = all.slice(i, i + 4);
      const statuses = await Promise.all(batch.map((s) => probe(s.path)));
      setResults((prev) => {
        const next = { ...prev };
        batch.forEach((s, j) => (next[s.key] = statuses[j]));
        return next;
      });
    }
    setScanning(false);
  };

  useEffect(() => {
    runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = GROUPS.flatMap((g) => g.slots).reduce(
    (acc, s) => {
      const st = results[s.key] || "pending";
      acc[st] = (acc[st] || 0) + 1;
      return acc;
    },
    { ok: 0, missing: 0, pending: 0 } as Record<Status, number>,
  );
  const totalSlots = GROUPS.flatMap((g) => g.slots).length;

  const copyMissing = () => {
    const missing = GROUPS.flatMap((g) => g.slots)
      .filter((s) => results[s.key] === "missing")
      .map((s) => `public${s.path}`)
      .join("\n");
    if (!missing) {
      toast({ title: "Nothing to copy", description: "All canonical images are uploaded." });
      return;
    }
    navigator.clipboard.writeText(missing);
    toast({ title: "Copied", description: `${totals.missing} missing file paths copied to clipboard.` });
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <Helmet>
        <title>Bath Image Upload Status | Aureum VII Trading</title>
        <meta name="description" content="Internal upload checklist for Bath & Sanitary product galleries." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <Navigation />

      <div className="border-b border-amber-500/10 bg-black/40 pt-28">
        <div className="container mx-auto px-6 py-4 text-xs text-neutral-500 lg:px-12">
          <LocalizedLink to="/bath-sanitary" className="hover:text-amber-300">
            Bath &amp; Sanitary
          </LocalizedLink>
          <span className="mx-2">/</span>
          <span className="text-neutral-300">Image Upload Status</span>
        </div>
      </div>

      <section className="border-b border-amber-500/10 py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">Internal · Asset Library</p>
          <h1 className="font-serif text-3xl text-neutral-50 sm:text-4xl">Bath Image Upload Status</h1>
          <p className="mt-4 max-w-2xl text-neutral-400">
            Live probe of every canonical image referenced by the Bath &amp; Sanitary product
            galleries. Missing files must be uploaded to <code className="text-amber-300">public/images/bath/</code> with
            the exact filename shown below.
          </p>

          {/* Summary */}
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Uploaded</p>
              <p className="mt-2 font-serif text-3xl text-emerald-200">{totals.ok} <span className="text-base text-neutral-500">/ {totalSlots}</span></p>
            </div>
            <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/80">Missing</p>
              <p className="mt-2 font-serif text-3xl text-amber-200">{totals.missing}</p>
            </div>
            <div className="rounded-sm border border-neutral-700 bg-neutral-900/40 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Pending</p>
              <p className="mt-2 font-serif text-3xl text-neutral-200">{totals.pending}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={runScan} disabled={scanning} className="bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500">
              {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {scanning ? "Scanning…" : "Re-scan"}
            </Button>
            <Button onClick={copyMissing} variant="outline" className="border-amber-500/40 bg-transparent text-amber-200 hover:bg-amber-500/10">
              <Copy className="mr-2 h-4 w-4" /> Copy missing paths
            </Button>
            <Button asChild variant="outline" className="border-neutral-700 text-neutral-200 hover:bg-neutral-900">
              <a href="/images/bath/README.md" target="_blank" rel="noopener noreferrer">
                Open README (filename reference) ↗
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Per-product breakdown */}
      <section className="py-12">
        <div className="container mx-auto space-y-10 px-6 lg:px-12">
          {GROUPS.map((g) => {
            const groupMissing = g.slots.filter((s) => results[s.key] === "missing").length;
            const groupOk = g.slots.filter((s) => results[s.key] === "ok").length;
            return (
              <div key={g.product} className="rounded-sm border border-neutral-800 bg-neutral-950/60">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 px-6 py-4">
                  <div>
                    <h2 className="font-serif text-xl text-neutral-100">{g.product}</h2>
                    <p className="mt-1 text-xs text-neutral-500">
                      {groupOk} / {g.slots.length} uploaded
                      {groupMissing > 0 && <span className="ml-2 text-amber-300">· {groupMissing} missing</span>}
                    </p>
                  </div>
                  {g.slug && (
                    <LocalizedLink
                      to={`/bath-sanitary/${g.slug}`}
                      className="text-xs text-amber-300 hover:text-amber-200"
                    >
                      View product page →
                    </LocalizedLink>
                  )}
                </div>

                <ul className="divide-y divide-neutral-800">
                  {g.slots.map((s) => {
                    const st = results[s.key] || "pending";
                    return (
                      <li key={s.key} className="flex flex-wrap items-center gap-4 px-6 py-3">
                        <div className="flex h-6 w-6 items-center justify-center">
                          {st === "ok" && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
                          {st === "missing" && <AlertCircle className="h-5 w-5 text-amber-400" />}
                          {st === "pending" && <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-neutral-200">{s.label}</p>
                          <p className="truncate font-mono text-[11px] text-neutral-500">
                            public{s.path}
                          </p>
                        </div>
                        <span
                          className={
                            "rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] " +
                            (st === "ok"
                              ? "bg-emerald-500/10 text-emerald-300"
                              : st === "missing"
                              ? "bg-amber-500/10 text-amber-300"
                              : "bg-neutral-800 text-neutral-400")
                          }
                        >
                          {st === "ok" ? "Uploaded" : st === "missing" ? "Missing" : "Checking"}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          <div className="rounded-sm border border-dashed border-amber-500/20 bg-neutral-900/40 p-5 text-xs text-neutral-500">
            Probe runs in the visitor's browser, so it reflects what end-users actually see.
            For a CI-time check, see <code className="text-amber-300">scripts/check-image-health.mjs</code>.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BathImageStatus;
